import { MfaMethodsTable } from "modules/users/business-users/account-view/MfaMethodsTable";
import { UsersTabs } from "modules/users/users-tabs.constants";
import { FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";

import {
  ActionButton,
  LabeledText,
  LabeledTextGroup,
  Separator,
  Stack,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize
} from "@bps/fluent-ui";
import { CopyableGuid } from "@components/CopyableText";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { useBpIdUserDetails } from "@libs/api/gateways/bp-id/bp-id.hooks";
import { PltUser } from "@libs/api/gateways/plt/plt-gateway.dtos";

import { UserBpIdIdentites } from "./UserBpIdIdentites";

interface UserBpIdProps {
  user: PltUser | undefined;
}

export const UserBpId: FunctionComponent<UserBpIdProps> = ({ user }) => {
  const navigate = useNavigate();

  const userDetailsQuery = useBpIdUserDetails(user?.bpIdUserId!, {
    enabled: !!user?.bpIdUserId
  });

  if (!user) return null;

  return (
    <Stack>
      <Separator />
      <QueryStateIndicator
        {...userDetailsQuery}
        noDataText={
          !user.bpIdUserId ? "User does not have Bp Id account" : undefined
        }
      >
        {bpUserDetails => {
          return (
            <LabeledTextGroup styles={{ root: { marginBottom: 24 } }}>
              <LabeledText wrap={false} label="Bp Id">
                <Stack horizontal>
                  <CopyableGuid value={bpUserDetails.id} short />
                  <TextBadge
                    badgeSize={TextBadgeSize.small}
                    badgeColor={
                      bpUserDetails.accountEnabled
                        ? TextBadgeColor.green
                        : TextBadgeColor.red
                    }
                  >
                    {bpUserDetails.accountEnabled ? "Enabled" : "Disabled"}
                  </TextBadge>
                </Stack>
              </LabeledText>

              <LabeledText wrap={false} label="First name">
                {bpUserDetails.firstName}
              </LabeledText>

              <LabeledText wrap={false} label="Last name">
                {bpUserDetails.lastName}
              </LabeledText>

              <LabeledText wrap={false} label="Sign-in email">
                {bpUserDetails.signInEmail}
              </LabeledText>

              <UserBpIdIdentites bpUserDetails={bpUserDetails} />

              <LabeledText wrap={false} label="MFA methods">
                {bpUserDetails.mfaMethods?.length ? (
                  <MfaMethodsTable
                    bpIdUserId={user.bpIdUserId!}
                    methods={bpUserDetails.mfaMethods}
                    refresh={() => userDetailsQuery.refetch()}
                    hideRemoveActions
                  />
                ) : (
                  undefined
                )}
              </LabeledText>
            </LabeledTextGroup>
          );
        }}
      </QueryStateIndicator>

      {user.bpIdUserId && (
        <ActionButton
          iconProps={{ iconName: "NavigateForward" }}
          onClick={() =>
            navigate(
              `/users/${UsersTabs.BusinessUsers.IdentitySearch.id}/${user.bpIdUserId}`
            )
          }
          styles={{ root: { alignSelf: "start", padding: 0, height: "auto" } }}
        >
          See full record
        </ActionButton>
      )}
    </Stack>
  );
};
